import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Layout from "../../layout/builders/Layout";
import { CheckCircle, Shield, Star, Users, TrendingUp, Clock, CreditCard } from "lucide-react";
import { axiosInstancev1 } from "../../lib/axiosInstance";
import useAuthStore from "../../store/useAuthStore";
import toast from "react-hot-toast";
import { load } from "@cashfreepayments/cashfree-js";

const BuilderConsultantSubscription = ({ excludeLayout = false }) => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { token } = useAuthStore();
    const [loading, setLoading] = useState(false);
    const [hasProfile, setHasProfile] = useState(false);
    const [subscriptionData, setSubscriptionData] = useState(null);
    const [cashfree, setCashfree] = useState(null);

    // Pricing constants for builder consultant subscription
    const BASE_AMOUNT = 1.0; // Base subscription price (₹)
    const CGST_RATE = 9;     // 9%
    const SGST_RATE = 9;     // 9%

    const baseAmount = BASE_AMOUNT;
    const cgstAmount = parseFloat((baseAmount * (CGST_RATE / 100)).toFixed(2));
    const sgstAmount = parseFloat((baseAmount * (SGST_RATE / 100)).toFixed(2));
    const total = parseFloat((baseAmount + cgstAmount + sgstAmount).toFixed(2));

    const formatCurrency = (value) =>
        new Intl.NumberFormat("en-IN", {
            style: "currency",
            currency: "INR",
            minimumFractionDigits: 2,
        }).format(value ?? 0);

    useEffect(() => {
        checkConsultantProfile();
        initializeCashfree();
    }, []);

    // Handle redirect back from Cashfree payment
    useEffect(() => {
        const orderId = searchParams.get("order_id");
        if (orderId) {
            verifyPayment(orderId);
        }
    }, [searchParams]);

    const initializeCashfree = async () => {
        try {
            const mode = process.env.REACT_APP_CASHFREE_MODE || "production";
            const cf = await load({ mode });
            setCashfree(cf);
        } catch (error) {
            console.error("Failed to load Cashfree SDK:", error);
        }
    };

    const checkConsultantProfile = async () => {
        try {
            const response = await axiosInstancev1.get("/builder/consultant-profile", {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success && response.data.profile) {
                setHasProfile(true);
                setSubscriptionData(response.data.profile);
            } else {
                setHasProfile(false);
                toast.error("Please create your consultant profile first");
                navigate("/builder/consultant-profile-create");
            }
        } catch (error) {
            if (error.response?.status === 404) {
                toast.error("Please create your consultant profile first");
                navigate("/builder/consultant-profile-create");
            }
        }
    };

    const verifyPayment = async (orderId) => {
        const toastId = toast.loading("Verifying payment...");
        try {
            const response = await axiosInstancev1.post("/builder/consultant-subscription/cashfree-verify", {
                orderId
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.dismiss(toastId);
            if (response.data.success) {
                toast.success("Subscription activated successfully!");
                setTimeout(() => navigate("/builder/consultant-profile-create"), 1500);
            } else {
                toast.error(response.data.message || "Payment verification failed");
            }
        } catch (error) {
            toast.dismiss(toastId);
            toast.error(error.response?.data?.message || "Payment verification failed");
        }
    };

    const handleSubscribe = async () => {
        if (!cashfree) {
            toast.error("Payment system not ready. Please refresh.");
            return;
        }
        try {
            setLoading(true);
            const totalAmount = total; // amount including CGST + SGST
            const returnUrl = `${window.location.origin}/builder/consultant-profile-subscription`;

            const response = await axiosInstancev1.post(
                "/builder/consultant-subscription/cashfree-order",
                {
                    amount: totalAmount,
                    returnUrl,
                },
                {
                    headers: { Authorization: `Bearer ${token}` },
                }
            );

            if (!response.data.success || !response.data.orderDetails?.payment_session_id) {
                toast.error("Failed to initiate payment. Please try again.");
                return;
            }

            const { payment_session_id } = response.data.orderDetails;
            cashfree.checkout({
                paymentSessionId: payment_session_id,
                redirectTarget: "_self"
            }).then((result) => {
                if (result.error) {
                    toast.error(result.error.message || "Payment failed");
                    setLoading(false);
                }
            });
        } catch (error) {
            console.error("Error creating subscription:", error);
            toast.error(error.response?.data?.message || "Failed to initiate payment");
            setLoading(false);
        }
    };


    const features = [
        {
            icon: <Users className="text-blue-600" size={20} />,
            title: "Visibility on Consultant Directory",
            description: "Your profile will be visible to all builders looking for consultants"
        },
        {
            icon: <Star className="text-yellow-600" size={20} />,
            title: "Priority Listing",
            description: "Active subscribers appear at the top of search results"
        },
        {
            icon: <Shield className="text-green-600" size={20} />,
            title: "Profile Verification Badge",
            description: "Get a verified badge on your consultant profile"
        },
        {
            icon: <TrendingUp className="text-purple-600" size={20} />,
            title: "Monthly Performance Insights",
            description: "Track profile views and contact requests"
        },
        {
            icon: <Users className="text-indigo-600" size={20} />,
            title: "Direct Contact from Clients",
            description: "Builders can directly contact you for consulting services"
        },
        {
            icon: <Clock className="text-orange-600" size={20} />,
            title: "24/7 Priority Customer Support",
            description: "Get dedicated support for all your queries"
        }
    ];

    const isActive = subscriptionData?.subscription_status === 'active';

    const Content = (
        <div className={`flex flex-col ${!excludeLayout ? 'min-h-screen bg-gray-50' : ''}`}>
            {!excludeLayout && (
                /* Sticky page header */
                <div className="sticky top-0 z-20 bg-white border-b border-gray-200">
                    <div className="mx-auto max-w-5xl px-4 py-4 flex items-center justify-between">
                        <div>
                            <h1 className="text-lg sm:text-xl font-semibold text-gray-900">
                                Consultant Subscription
                            </h1>
                            <p className="text-xs sm:text-sm text-gray-500 mt-1">
                                Keep your consultant profile active and visible to builders.
                            </p>
                        </div>
                        <button
                            onClick={() => navigate("/builder/consultant-profile-subscription-history")}
                            className="inline-flex items-center gap-2 rounded-full border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100 transition"
                        >
                            View History
                        </button>
                    </div>
                </div>
            )}

            <div className={`mx-auto max-w-5xl px-4 pb-8 w-full ${excludeLayout ? 'pt-4' : ''}`}>
                {/* Current Status */}
                {isActive && subscriptionData?.validity_end_date && (
                    <div className="mt-8 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm border-l-4 border-l-blue-600">
                        <div className="p-5 sm:p-6 flex flex-col md:flex-row md:items-center justify-between gap-6">
                            <div className="flex items-start gap-4">
                                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-blue-50">
                                    <Shield className="h-6 w-6 text-blue-600" />
                                </div>
                                <div className="space-y-1">
                                    <div className="flex items-center gap-2">
                                        <h3 className="text-lg font-bold text-gray-900">
                                            Ducktail Consultant Membership
                                        </h3>
                                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide text-emerald-600 border border-emerald-100">
                                            Active
                                        </span>
                                    </div>
                                    <p className="text-sm text-gray-500 font-medium">
                                        Your profile is published and visible in the consultant directory.
                                    </p>
                                    <div className="pt-2 flex flex-wrap gap-4 text-xs font-medium text-gray-400 uppercase tracking-widest">
                                        <div className="flex items-center gap-1.5">
                                            <Clock className="h-3.5 w-3.5" />
                                            <span>Expires {new Date(subscriptionData.validity_end_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                            <Star className="h-3.5 w-3.5" />
                                            <span>Verified Consultant</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="flex flex-col items-end gap-1">
                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Membership Status</span>
                                <div className="flex items-center gap-2 px-4 py-2 bg-gray-50 rounded-lg border border-gray-100">
                                    <CheckCircle size={16} className="text-emerald-500" />
                                    <span className="text-sm font-bold text-gray-700">Fully Verified</span>
                                </div>
                                <button
                                    onClick={() => navigate("/builder/consultant-profile-subscription-history")}
                                    className="text-[11px] font-medium text-blue-600 hover:underline mt-1"
                                >
                                    View Billing History
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Simple review + total section when not subscribed */}
                {!isActive && (
                    <>
                        {/* Top blue banner similar to builder subscription page but simpler */}
                        <div className="mt-6 rounded-2xl bg-gradient-to-r from-blue-500 to-blue-600 text-white p-6 shadow-md">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                <div>
                                    <div className="inline-flex items-center gap-2 text-xs bg-white/15 rounded-full px-3 py-1">
                                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-300" />
                                        Step 1: Review plan
                                        <span className="mx-1 text-white/70">•</span>
                                        Step 2: Pay securely
                                    </div>
                                    <h2 className="mt-3 text-2xl font-bold">
                                        Review & Complete Your Consultant Subscription
                                    </h2>
                                    <p className="mt-2 text-sm text-blue-100 max-w-xl">
                                        Confirm your annual consultant subscription and proceed to secure online payment.
                                    </p>
                                </div>
                                <div className="rounded-xl bg-white/20 px-4 py-3 text-right">
                                    <p className="text-xs uppercase tracking-wide text-white/80">Total Due</p>
                                    <p className="mt-1 text-2xl font-extrabold">
                                        {formatCurrency(total)}
                                    </p>
                                    <p className="mt-1 text-[11px] text-blue-100">
                                        Inclusive of CGST & SGST
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Main content: features + order summary */}
                        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-5">
                            {/* Left: Benefits / features */}
                            <div className="md:col-span-2 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                                <div className="flex items-center gap-2 mb-4">
                                    <Shield className="text-blue-600" size={18} />
                                    <h3 className="text-sm font-semibold text-gray-900">
                                        What you get with this subscription
                                    </h3>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    {features.map((feature, index) => (
                                        <div key={index} className="flex gap-3">
                                            <div className="w-9 h-9 bg-gray-50 rounded-lg flex items-center justify-center flex-shrink-0">
                                                {feature.icon}
                                            </div>
                                            <div>
                                                <p className="text-sm font-semibold text-gray-900">
                                                    {feature.title}
                                                </p>
                                                <p className="mt-1 text-xs text-gray-500">
                                                    {feature.description}
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div className="mt-5 border-t border-dashed border-gray-200 pt-4 space-y-2 text-sm text-gray-700">
                                    <p>
                                        • Your profile appears in the consultant directory for 12 months.
                                    </p>
                                    <p>
                                        • Builders can contact you directly for consulting services.
                                    </p>
                                    <p>
                                        • You can renew easily from this same page before expiry.
                                    </p>
                                </div>
                            </div>

                            {/* Right: Order summary */}
                            <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                                <h3 className="text-sm font-semibold text-gray-900 mb-4">Order Summary</h3>
                                <div className="space-y-2 text-sm">
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Subscription Amount</span>
                                        <span className="font-semibold text-gray-900">
                                            {formatCurrency(baseAmount)}
                                        </span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">
                                            CGST ({CGST_RATE}%)
                                        </span>
                                        <span className="font-semibold text-gray-900">
                                            {formatCurrency(cgstAmount)}
                                        </span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">
                                            SGST ({SGST_RATE}%)
                                        </span>
                                        <span className="font-semibold text-gray-900">
                                            {formatCurrency(sgstAmount)}
                                        </span>
                                    </div>
                                    <div className="pt-2 border-t border-gray-200 text-xs text-gray-500">
                                        Billed to: Ducktail Private Limited<br />
                                        Tamilnadu - 627105
                                    </div>
                                </div>

                                <div className="mt-4 pt-3 border-t border-gray-200">
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm font-semibold text-gray-900">
                                            Total Due
                                        </span>
                                        <span className="text-xl font-bold text-blue-600">
                                            {formatCurrency(total)}
                                        </span>
                                    </div>
                                    <p className="mt-1 text-[11px] text-gray-400">
                                        Includes all applicable taxes
                                    </p>
                                </div>

                                <button
                                    onClick={handleSubscribe}
                                    disabled={loading}
                                    className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-3 text-sm font-semibold text-white hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <CreditCard size={16} />
                                    {loading ? "Processing..." : `Pay ${formatCurrency(total)} & Subscribe`}
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );

    if (excludeLayout) return Content;

    return (
        <Layout>
            {Content}
        </Layout>
    );
};

export default BuilderConsultantSubscription;
